import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Alert from '@material-ui/lab/Alert';
import SearchIcon from '@material-ui/icons/Search';
import RestoreIcon from '@material-ui/icons/Restore';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import UnsubscribeIcon from '@material-ui/icons/Unsubscribe';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import EmailIcon from '@material-ui/icons/Email';
import { useHistory } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  statCard: {
    textAlign: 'center',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      '& .MuiCardContent-root': {
        padding: theme.spacing(1.5),
      },
    },
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  searchField: {
    width: 400,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  tableContainer: {
    maxHeight: 600,
    [theme.breakpoints.down('sm')]: {
      overflowX: 'auto',
      maxHeight: 'none',
    },
  },
  reasonChip: {
    marginBottom: theme.spacing(0.5),
  },
  campaignCell: {
    maxWidth: 200,
  },
}));

const ManageUnsubscribes = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [unsubscribes, setUnsubscribes] = useState([]);
  const [filteredUnsubscribes, setFilteredUnsubscribes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [resubscribeDialog, setResubscribeDialog] = useState({ open: false, email: null });
  const [stats, setStats] = useState({
    total: 0,
    tooFrequent: 0,
    notRelevant: 0,
    neverSignedUp: 0,
    privacyConcerns: 0,
    technicalIssues: 0,
    other: 0,
    topCampaigns: [],
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchUnsubscribes();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = unsubscribes.filter(
        (item) =>
          item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.campaign_name && item.campaign_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.reason_category && item.reason_category.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.reason_other && item.reason_other.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUnsubscribes(filtered);
    } else {
      setFilteredUnsubscribes(unsubscribes);
    }
  }, [searchTerm, unsubscribes]);

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      tooFrequent: 0,
      notRelevant: 0,
      neverSignedUp: 0,
      privacyConcerns: 0,
      technicalIssues: 0,
      other: 0,
      topCampaigns: [],
    };

    // Count by reason category
    data.forEach(item => {
      if (item.reason_category) {
        switch (item.reason_category) {
          case 'too-frequent':
            stats.tooFrequent++;
            break;
          case 'not-relevant':
            stats.notRelevant++;
            break;
          case 'never-signed-up':
            stats.neverSignedUp++;
            break;
          case 'privacy-concerns':
            stats.privacyConcerns++;
            break;
          case 'technical-issues':
            stats.technicalIssues++;
            break;
          case 'other':
            stats.other++;
            break;
        }
      }
    });

    // Get top campaigns causing unsubscribes
    const campaignCounts = {};
    data.forEach(item => {
      if (item.campaign_name) {
        campaignCounts[item.campaign_name] = (campaignCounts[item.campaign_name] || 0) + 1;
      }
    });

    stats.topCampaigns = Object.entries(campaignCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    return stats;
  };

  const fetchUnsubscribes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}email-campaigns/unsubscribes`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const unsubscribeList = response.data.unsubscribes || [];
      setUnsubscribes(unsubscribeList);
      setFilteredUnsubscribes(unsubscribeList);
      setStats(calculateStats(unsubscribeList));
    } catch (error) {
      console.error('Error fetching unsubscribes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch unsubscribed users',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResubscribeClick = (email) => {
    setResubscribeDialog({ open: true, email });
  };

  const handleResubscribeConfirm = async () => {
    const email = resubscribeDialog.email;
    setResubscribeDialog({ open: false, email: null });

    try {
      await axios.delete(`${API_URL}email-campaigns/unsubscribe/${email}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSnackbar({
        open: true,
        message: `${email} has been resubscribed successfully`,
        severity: 'success',
      });
      fetchUnsubscribes(); // Refresh the list
    } catch (error) {
      console.error('Error resubscribing user:', error);
      setSnackbar({
        open: true,
        message: 'Failed to resubscribe user',
        severity: 'error',
      });
    }
  };

  const handleResubscribeCancel = () => {
    setResubscribeDialog({ open: false, email: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatReasonCategory = (category) => {
    const map = {
      'too-frequent': 'Too Many Emails',
      'not-relevant': 'Not Relevant',
      'never-signed-up': 'Never Signed Up',
      'privacy-concerns': 'Privacy Concerns',
      'technical-issues': 'Technical Issues',
      'other': 'Other',
    };
    return map[category] || category;
  };

  const getReasonColor = (category) => {
    const colorMap = {
      'too-frequent': 'secondary',
      'not-relevant': 'default',
      'never-signed-up': 'primary',
      'privacy-concerns': 'secondary',
      'technical-issues': 'default',
      'other': 'default',
    };
    return colorMap[category] || 'default';
  };

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center">
          <Tooltip title="Back to Campaigns">
            <IconButton onClick={() => history.push('/email-campaigns')} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h4" style={{ marginLeft: 8, fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
            Manage Unsubscribes
          </Typography>
        </Box>
        <Chip
          label={`Total: ${stats.total}`}
          color="primary"
          size="medium"
          icon={<UnsubscribeIcon />}
        />
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} className={classes.statsContainer}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Too Many Emails
              </Typography>
              <Typography className={classes.statNumber}>
                {stats.tooFrequent}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.total > 0 ? ((stats.tooFrequent / stats.total) * 100).toFixed(1) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Not Relevant
              </Typography>
              <Typography className={classes.statNumber}>
                {stats.notRelevant}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.total > 0 ? ((stats.notRelevant / stats.total) * 100).toFixed(1) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Privacy Concerns
              </Typography>
              <Typography className={classes.statNumber}>
                {stats.privacyConcerns}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.total > 0 ? ((stats.privacyConcerns / stats.total) * 100).toFixed(1) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Other Reasons
              </Typography>
              <Typography className={classes.statNumber}>
                {stats.other + stats.neverSignedUp + stats.technicalIssues}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.total > 0 ? (((stats.other + stats.neverSignedUp + stats.technicalIssues) / stats.total) * 100).toFixed(1) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {stats.topCampaigns.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" marginBottom={2}>
                  <EmailIcon color="primary" style={{ marginRight: 8 }} />
                  <Typography variant="h6">
                    Top Campaigns with Unsubscribes
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {stats.topCampaigns.map((campaign, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" padding={1} bgcolor="#f5f5f5" borderRadius={1}>
                        <Typography variant="body2" noWrap style={{ flex: 1, marginRight: 8 }}>
                          {campaign.name}
                        </Typography>
                        <Chip label={campaign.count} size="small" color="secondary" />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <TextField
        className={classes.searchField}
        variant="outlined"
        placeholder="Search by email, campaign, or reason..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Unsubscribed At</TableCell>
              <TableCell>Reason Category</TableCell>
              <TableCell>Additional Details</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredUnsubscribes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {searchTerm
                      ? 'No unsubscribes found matching your search.'
                      : 'No unsubscribed users found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUnsubscribes.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.email}</TableCell>
                  <TableCell className={classes.campaignCell}>
                    {item.campaign_name ? (
                      <Tooltip title={item.campaign_name}>
                        <Chip
                          label={item.campaign_name}
                          size="small"
                          icon={<EmailIcon />}
                          style={{ maxWidth: '100%' }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(item.unsubscribed_at)}</TableCell>
                  <TableCell>
                    {item.reason_category ? (
                      <Chip
                        label={formatReasonCategory(item.reason_category)}
                        size="small"
                        color={getReasonColor(item.reason_category)}
                        className={classes.reasonChip}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Not provided
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.reason_other ? (
                      <Tooltip title={item.reason_other}>
                        <Typography
                          variant="body2"
                          style={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.reason_other}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Resubscribe">
                      <IconButton
                        size="small"
                        onClick={() => handleResubscribeClick(item.email)}
                        color="primary"
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Resubscribe Confirmation Dialog */}
      <Dialog
        open={resubscribeDialog.open}
        onClose={handleResubscribeCancel}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle>Confirm Resubscribe</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to resubscribe "{resubscribeDialog.email}"? This will
            allow them to receive future email campaigns again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResubscribeCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleResubscribeConfirm}
            color="primary"
            variant="contained"
          >
            Resubscribe
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ManageUnsubscribes;
